// Third-party
import moment from 'moment';

// Utils
import { DISCLAIMER_KEYS } from './constants';
import { ROLLING_INTERVALS } from '../portfolios';
import { ChartItem, getRollingIntervalFromDates } from '../charts';
import { formatLocalizedString } from '../i18n';
import { COMPARISON_ITEM_TYPES } from '../constants';
import { getComparisonItemHoldings } from '../comparison/items';
import RollingPerformance from '@/types/rolling-performance';
import Nullable from '@/types/nullable';

export const itemIsMissingData = (item: ChartItem, interval: ROLLING_INTERVALS, startDate: Nullable<Date>, endDate: Date, shortHistory?: boolean): boolean => {
  // Skip benchmarks for now -- add back in CON-1038.
  if (item.type === COMPARISON_ITEM_TYPES.BENCHMARK) {
    return false;
  }

  if (item.isShort && shortHistory) {
    return false;
  }

  const startTimestamp = startDate && startDate.getTime();
  const endTimestamp = endDate.getTime();

  return getComparisonItemHoldings(item, true).some(holding => {
    let dataPoints: RollingPerformance | null = null;

    if (holding.rolling && holding.rolling[interval] && holding.rolling[interval].length) {
      dataPoints = holding.rolling;
    } else if (holding.nav && holding.nav[interval] && holding.nav[interval].length) {
      dataPoints = holding.nav;
    }

    if (dataPoints == null) {
      return true;
    }

    const intervalDataPoints = dataPoints[interval];
    const dataStart = moment(intervalDataPoints[0][0]);
    const dataEnd = moment(intervalDataPoints[intervalDataPoints.length - 1][0]);

    // Validate leading data (start of the chart)
    if (startTimestamp) {
      // For weekly data, check if there's any expected weekly data that's missing
      if (interval === ROLLING_INTERVALS.WEEKLY) {
        const firstExpectedDate = moment(startTimestamp).utc();

        if (firstExpectedDate.isoWeekday() <= 5) {
          firstExpectedDate.isoWeekday(5);
        } else {
          firstExpectedDate.add(1, 'week').isoWeekday(5);
        }

        if (firstExpectedDate.isBefore(dataStart, 'day')) {
          return true;
        }
      } else if (interval === ROLLING_INTERVALS.MONTHLY) {
        const firstExpectedDate = moment(startTimestamp).utc().endOf('month');

        if (firstExpectedDate.isBefore(dataStart, 'day')) {
          return true;
        }
      }
    }

    // Validate trailing data
    if (interval === ROLLING_INTERVALS.WEEKLY) {
      const lastExpectedDate = moment(endTimestamp).utc();

      if (lastExpectedDate.isoWeekday() < 5) {
        lastExpectedDate.subtract(1, 'week').isoWeekday(5);
      } else {
        lastExpectedDate.isoWeekday(5);
      }

      if (lastExpectedDate.isAfter(dataEnd, 'day')) {
        return true;
      }
    } else if (interval === ROLLING_INTERVALS.MONTHLY) {
      const lastExpectedDate = moment(endTimestamp).subtract(1, 'month').utc().endOf('month');

      if (lastExpectedDate.isAfter(dataEnd, 'day')) {
        return true;
      }
    }

    return false;
  });
};

export const itemIsMissingWeeklyData = (item: ChartItem, startDate: Nullable<Date>, endDate: Date): boolean => {
  // Right now we always use monthly data if start date is not defined. This disclaimer wouldn't apply.
  if (!startDate) {
    return false;
  }

  return item.holdings.some(holding => {
    const monthly = holding.rolling?.monthly; // eslint-disable-line prefer-destructuring
    const weekly = holding.rolling?.weekly; // eslint-disable-line prefer-destructuring

    const firstMonthly = monthly && monthly.length > 0 ? monthly[0] : null;
    const firstWeekly = weekly && weekly.length > 0 ? weekly[0] : null;

    const lastMonthly = monthly && monthly.length > 0 ? monthly[monthly.length - 1] : null;
    const lastWeekly = weekly && weekly.length > 0 ? weekly[weekly.length - 1] : null;

    if (!firstWeekly || !firstMonthly || !lastWeekly || !lastMonthly) {
      return false;
    }

    const start = moment(startDate);
    const end = moment(endDate);

    const momentFirstMonth = moment(firstMonthly[0]);
    const momentFirstWeek = moment(firstWeekly[0]);

    if (start.isBefore(momentFirstWeek, 'week') && momentFirstWeek.diff(momentFirstMonth, 'month') > 1) {
      // Weekly rolling starts after the chart's start date - force monthly.
      return true;
    }

    const momentLastMonth = moment(lastMonthly[0]);
    const momentLastWeek = moment(lastWeekly[0]);

    if (end.isAfter(momentLastWeek, 'week') && momentLastMonth.diff(momentLastWeek, 'month') > 1) {
      // Weekly rolling ends before chart's end date - force monthly.
      return true;
    }

    return false;
  });
};

/**
 * Generates the disclaimer related to missing nav/rolling data, if any.
 * NOTE: NAV is used to generate our rollings data so check that first
 *
 * @param items Items to be graphed.
 * @param interval Data interval (monthly or weekly).
 * @param startDate Start date of the graph.
 * @param endDate End date of the graph.
 */
export const getGraphPerformanceDisclaimer = (
  items: ChartItem[],
  interval: ROLLING_INTERVALS,
  startDate: Nullable<Date>,
  endDate: Date,
  shortHistory?: boolean,
  useMonthly?: boolean,
) => {
  if (useMonthly) {
    const expectedInterval = getRollingIntervalFromDates(startDate, endDate, items, useMonthly, shortHistory);

    // Only show a disclaimer if we're supposed to be showing weekly data points based on the selected dates.
    if (expectedInterval === ROLLING_INTERVALS.WEEKLY) {
      const itemsMissingWeeklyData = items.filter(item => itemIsMissingWeeklyData(item, startDate, endDate));
      if (itemsMissingWeeklyData.length > 0) {
        return {
          key: DISCLAIMER_KEYS.CHART_ITEMS_MISSING_WEEKLY_DATA,
          data: {
            list: itemsMissingWeeklyData.map(item => formatLocalizedString(item.name)),
          },
        };
      }
    }
  }

  const itemsMissingData = items.filter(item => itemIsMissingData(item, interval, startDate, endDate, shortHistory));

  if (itemsMissingData.length > 0) {
    return {
      key: DISCLAIMER_KEYS.GROWTH_CHART_NOT_ENOUGH_DATA,
      data: {
        list: itemsMissingData.map(item => formatLocalizedString(item.name)),
      },
    };
  }

  return null;
};
